import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Footer } from "../components/ui/footer";
import { Hero } from "../components/ui/hero";
import { usdToLocalLabel } from "../utilities/currency";
import { money } from "../utilities/currency";
import { readingLine } from "../utilities/currency";
import { FestivalAds } from "../components/ui/festivalads";
import { Blogs } from "../components/ui/blogs";
import { Header } from "../components/ui/header";
import { PaymentBadges } from "../components/paymentbadges.tsx";
import { BuyMinutesSection } from "../components/buyminutessection.tsx";
import Agents from "../components/agents.tsx";
import axios from "axios";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import {
  Sun,
  Moon,
  Star,
  Play,
  MessageCircle,
  Phone,
  Video,
  Heart,
  Eye,
  BookOpen,
  Sparkles,
  Feather,
  Gem,
  Moon as MoonIcon,
  Flower,
  Gift,
  BadgeCheck,
  Wallet as WalletIcon,
  CreditCard,
  Banknote,
} from "lucide-react";



/**********************
 * TEKSTER (NORSK)
 **********************/
const T = {
  offer: "✨ Spesialtilbud – Betal 99 kr, få 198 kr i ringe kreditt! ✨",
  signIn: "Logg inn",
  join: "Bli med",
  allAdvisors: "Alle veiledere",
  festivalTitle: "Festivalkampanjer",
  referTitle: "Verv & få gratis reading!",
  referBody: "Verv 5 betalende kunder og få en GRATIS 15‑min reading hos en toppveileder.",
  blogTitle: "Fra Mystic‑bloggen",
  viewAll: "Se alle",
  subscribeLine: "Få nye guider og tilbud på e‑post.",
  read: "Les",
  invite: "Inviter venner",
  sponsored: "Sponset",
  ad: (n: number) => `Annonse ${n}`,
  adBlurb: "Annonser produkter, apper eller arrangementer her.",
  learnMore: "Les mer",
  connectNow: "Koble til nå",
  from: "fra",
  minutes: "min",
  verified: "Verifisert",
  categories: [
    "Kjærlighetsreading",
    "Klarsynt reading",
    "Tarot‑reading",
    "Astrologi & Horoskop",
    "Englebudskap",
    "Orakel‑veiledning",
    "Drømmetydning",
    "Åndelig medium",
  ],
  hero: {
    name: "Advokatene",
    tag: "Kjærlighetsekspert",
    blurb:
      "Mitt løfte: Jeg er her for deg. Når det er vanskelig å vite hvem man kan stole på, er ekte nærvær alt.",
    getReading: "Bestill konsultasjon",
  },
  footer: {
    getApp: "Få appen for klarhet hvor du enn er.",
    explore: "Utforsk",
    special: "Spesialiteter",
    more: "Mer",
    advisors: "Veiledere",
    blog: "Bloggen vår",
    applyCode: "Bruk kampanjekode",
    support: "Kundesupport",
    about: "Om oss",
    apply: "Veileder? Søk her",
    privacy: "Personvern",
    terms: "Vilkår",
  },
  wallet: {
    title: "Kjøp ringeminutter",
    intro:
      "Minutter lagres i din Mystic‑wallet og kan brukes hos alle veiledere (chat / samtale / video).",
    numLabel: "Telefonnummeret du ringer fra",
    amount: "Velg beløp",
    buy: (amt: number) => `Kjøp ${money(amt)} kreditt`,
    learn: "Les mer om betalingsmåter",
    err: "Skriv inn et gyldig telefonnummer",
    otp: (n: string) => `Kode sendt til ${n}. Skriv den inn for å bekrefte.`,
    enterOtp: "Skriv inn kode",
    verify: "Bekreft & legg til kreditt",
    cancel: "Avbryt",
    success: (amt: number) => `${money(amt)} lagt til i lommeboken!`,
    pm1: "Vipps MobilePay",
    pm2: "Kort (krever registrering på ditt mobilnummer)",
    nolgin: "Ingen innlogging nødvendig for Vipps MobilePay – ring direkte.",
  },
};

/**********************
 * SMÅHJELPERE (NORSK)
 **********************/

function CategorySection() {
  const labels = T.categories;
  const icons = [
    <Heart className="w-6 h-6 text-red-400" key="h" />,
    <Eye className="w-6 h-6 text-purple-400" key="e" />,
    <BookOpen className="w-6 h-6 text-blue-400" key="b" />,
    <Sparkles className="w-6 h-6 text-cyan-500" key="s" />,
    <Feather className="w-6 h-6 text-pink-400" key="f" />,
    <Gem className="w-6 h-6 text-yellow-500" key="g" />,
    <MoonIcon className="w-6 h-6 text-indigo-400" key="m" />,
    <Flower className="w-6 h-6 text-green-500" key="fl" />,
  ];
  const pillColors = [
    "bg-red-100 text-red-700",
    "bg-purple-100 text-purple-700",
    "bg-blue-100 text-blue-700",
    "bg-cyan-100 text-cyan-700",
    "bg-pink-100 text-pink-700",
    "bg-yellow-100 text-yellow-700",
    "bg-indigo-100 text-indigo-700",
    "bg-green-100 text-green-700",
  ];
  return (
    <section className="py-12 px-4 max-w-6xl mx-auto">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {labels.map((name, idx) => (
          <div
            key={idx}
            className={`rounded-3xl p-6 flex flex-col items-center justify-center shadow-sm hover:shadow-md transition ${pillColors[idx]}`}
          >
            {icons[idx]}
            <p className="mt-3 font-semibold text-center">{name}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-lg md:text-xl font-semibold text-gray-900 text-center md:text-left">
          Fant du ikke det du lette etter? <span className="font-normal">{T.allAdvisors}</span>
        </p>
        <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-6 py-3">{T.allAdvisors}</Button>
      </div>
    </section>
  );
}




/***********************
 * LOMMEBOK – KJØP MINUTTER (Norsk)
 ***********************/


/***********************
 * HOVEDSIDEKOMPONENT
 ***********************/
function SpiritualMarketplace({ onLoginClick, currentUser, onLogout, baseUrl, onLoginSuccess }) {
  // const [darkMode, setDarkMode] = useState(() => {
  //   try { return (localStorage.getItem('darkMode')) || false; } catch { return false; }
  // });
  const [scrolled, setScrolled] = useState(false);
  const [wallet, setWallet] = useState(0);

  const client = useSelector((state) => state.client.client);
  const default_configs = client ? JSON.parse(client.default_configs) : "";
  const blog_page_settings = client ? JSON.parse(client.blog_page_settings) : "";

  useEffect(() => {
    const channel = new BroadcastChannel("chamcore-login");
    channel.onmessage = (event) => {
      if (event.data?.type === "LOGIN_APPROVED") {
        Swal.fire({
          icon: "success",
          title: "Logg inn godkjent",
          text: "Takk for godkjenningen.",
          showConfirmButton: true,
        });
        if (typeof onLoginSuccess === "function") {
          onLoginSuccess(event.data?.token, event.data?.role, event.data?.name);
        }
      }
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // const advisors = [
  //   { name: "Sondre", expertise: "Klarsynt veileder", rating: 4.9, count: 1608, since: 2025, status: "ONLINE", price: "$5.99/min", img: "https://randomuser.me/api/portraits/men/32.jpg", verified: true },
  //   { name: "Ingrid", expertise: "Healing gjennom reading", rating: 4.6, count: 1038, since: 2024, status: "ONLINE", price: "$1.99/min", img: "https://randomuser.me/api/portraits/women/44.jpg", verified: false },
  //   { name: "Mats", expertise: "Klarsynt veileder", rating: 5.0, count: 4931, since: 2016, status: "BUSY", price: "$3.99/min", img: "https://randomuser.me/api/portraits/men/76.jpg", verified: true },
  //   { name: "Linn", expertise: "Klarhør/clairaudient", rating: 4.9, count: 29893, since: 2022, status: "ONLINE", price: "$2.99/min", img: "https://randomuser.me/api/portraits/women/65.jpg", verified: false },
  //   { name: "Anette", expertise: "Håndlesning", rating: 4.7, count: 3210, since: 2019, status: "ONLINE", price: "$1.49/min", img: "https://randomuser.me/api/portraits/women/47.jpg", verified: true },
  //   { name: "Henrik", expertise: "Numerologi‑spesialist", rating: 4.8, count: 7800, since: 2017, status: "BUSY", price: "$2.49/min", img: "https://randomuser.me/api/portraits/men/45.jpg", verified: false },
  //   { name: "Maja", expertise: "Drømmetyder", rating: 4.6, count: 5400, since: 2021, status: "ONLINE", price: "$1.99/min", img: "https://randomuser.me/api/portraits/women/63.jpg", verified: false },
  //   { name: "Kasper", expertise: "Reiki‑healer", rating: 5.0, count: 6540, since: 2018, status: "ONLINE", price: "$2.79/min", img: "https://randomuser.me/api/portraits/men/53.jpg", verified: true },
  // ];



  useEffect(() => {
    if (currentUser?.role === "customer") {
      axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

        .then((response) => {
          if (response.data.status === "OK") {
            setWallet(response.data.data.remaining_amount);
          }
        });
    }
  })

  return (
    <div className={"bg-white text-gray-900"}>
      {/* Topplinje m/tilbud */}


      {/* Header (sticky ved scroll) */}
      {/* <header
        className={`sticky top-0 z-50 transition-shadow ${
          darkMode ? "bg-gray-900/80 text-gray-100" : "bg-white/80 text-gray-900"
        } backdrop-blur ${scrolled ? "shadow-md" : ""}`}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center p-4">
          <div className="flex items-center gap-2">
            <Menu className="w-6 h-6 text-purple-700" />
            <h1 className="text-xl font-bold"><Link to="/">MysticConnect</Link></h1>
          </div>
          <div className="flex items-center gap-4">
            <Translator />
            <button className="hover:text-purple-700 font-medium">{T.signIn}</button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-4 py-1 text-sm">{T.join}</Button>
          </div>
        </div>
      </header> */}
      <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} />

      {/* HERO – heldekkende bilde */}
      <Hero hero={T.hero} />

      {/* Kjøp minutter (Vipps/Kort) */}
      {default_configs.wallet_payment === "yes" ? (<BuyMinutesSection onTopUp={(amt) => setWallet((w) => w + amt)} T={T} baseUrl={baseUrl} />) : ('')}



      {/* Veiledere – to rader med annonser mellom */}
      <Agents baseUrl={baseUrl} T={T} currentUser={currentUser} onLoginClick={onLoginClick} onLoginSuccess={onLoginSuccess} />

      {/* Verv (15 min) */}
      <section className="py-12 px-4 bg-gradient-to-r from-pink-100 via-purple-100 to-indigo-100">
        <div className="max-w-4xl mx-auto text-center">
          <Gift className="w-12 h-12 text-purple-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{T.referTitle}</h2>
          <p className="text-gray-700 mb-6">{T.referBody}</p>
          <Button className="bg-pink-500 hover:bg-pink-600 text-white rounded-full px-6 py-3">{T.invite}</Button>
        </div>
      </section>

      {/* Festivalkampanjer */}
      <FestivalAds T={T} />

      {/* Kategorier */}
      <CategorySection />

      {/* Blogg */}
      {blog_page_settings.recent_blogs_section === "yes" ? (<Blogs T={T} />) : ('')}

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default SpiritualMarketplace;
